import React, { useState, useEffect, useRef } from 'react';
import { connect } from 'react-redux';

import { makeStyles } from '@material-ui/core/styles';
import { XGrid } from '@material-ui/x-grid';
import Fab from '@material-ui/core/Fab';

import InfoIcon from '@material-ui/icons/Info';
import AddIcon from '@material-ui/icons/Add';
import DeleteIcon from '@material-ui/icons/Delete';
import EditIcon from '@material-ui/icons/Edit';

import PrintsSearch2 from 'components/RMSSearch/components/PrintsSearch2';
import Tooltip from 'components/Tooltip';

import { displayDateTime } from 'reducers/TimeReducer';
import { showDisplayDataWindow, addBulletin, editBulletin } from 'reducers/PersonDialogsReducer';
import { handleError } from 'reducers/ErrorReducer';
import { findBulletinsData, removeAlert } from 'reducers/BulletinReducer';
import { notify } from 'reducers/NotifierReducer';
import { getFullPermissions } from 'reducers/PermissionsReducer';

const columns = [
  { field: 'AlertType', headerName: 'Alert Type', width: 150 },
  { field: 'AgencyID', headerName: 'AgencyID', width: 150 },

  {
    field: 'Beginning',
    valueFormatter: (params) => displayDateTime(params.value),
    width: 200,
  },
  {
    field: 'Ending',
    valueFormatter: (params) => displayDateTime(params.value),
    width: 200,
  },
  { field: 'Person', headerName: 'Person', width: 150 },
  { field: 'OLN', headerName: 'OLN', width: 150 },
  { field: 'OLNState', headerName: 'OLN State', width: 150 },
  { field: 'Plate', headerName: 'Plate', width: 150 },
  { field: 'PlateState', headerName: 'Plate State', width: 150 },
  { field: 'Address', headerName: 'Address', width: 200 },
  { field: 'Ranges', headerName: 'Ranges', width: 200 },
  { field: 'Notification', headerName: 'Notification', width: 150 },
  { field: 'Priority', headerName: 'Priority', width: 150 },
  {
    field: 'created',
    headerName: 'Created',
    width: 200,
    valueFormatter: (params) => displayDateTime(params.value),
    hide: false,
  },
  {
    field: 'createdBy',
    headerName: 'CreatedBy',
    width: 150,
    hide: false,
  },
  {
    field: 'updated',
    headerName: 'Updated',
    width: 200,
    valueFormatter: (params) => displayDateTime(params.value),
    hide: false,
  },
  {
    field: 'updatedBy',
    headerName: 'UpdatedBy',
    width: 150,
    hide: false,
  },
];

const useStyles = makeStyles((theme) => ({
  wrap: {
    padding: theme.spacing(3),
  },
  gridWrap: {
    width: '100%',
    boxSizing: 'border-box',
    height: 600,
  },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginBottom: theme.spacing(2),
    '& button': {
      marginLeft: theme.spacing(1),
    },
  },
}));

function Bulletins(props) {
  const { person, dataUpdate, onBulletinChange } = props;

  const classes = useStyles();

  const [data, setData] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [selection, setSelection] = useState(null);
  const [hiddenColumns, setHiddenColumns] = useState([]);
  const bulletinePermission = getFullPermissions('cad', 'Alerts', 'any');
  console.log('bulletinePermission', bulletinePermission);
  const mountedRef = useRef(true);

  /* get Bulletins for first time */
  useEffect(() => {
    getBulletins();
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  /* get bulletins when adding new bulletin */
  useEffect(() => {
    if (!dataUpdate || dataUpdate.type !== 'Bulletin') return;
    getBulletins();
  }, [dataUpdate]);

  const getBulletins = async () => {
    try {
      const data = await findBulletinsData({ ptsPersonID: person.ptsPersonID });
      if (!mountedRef.current) return;
      setData(processData(data));
      setLoaded(true);
      onBulletinChange?.(data.length);
    } catch (err) {
      props.handleError(err, 'Error receiving address bulletins data.');
    }
  };

  const processData = (data) => {
    return data.map((row) => ({
      ...row,
      id: row.ptsAlertID,
      created: row.Created,
      createdBy: row.CreatedBy,
      updated: row.Updated,
      updatedBy: row.UpdatedBy,
    }));
  };

  const viewDetails = () => {
    const item = data.find((row) => row.id === selection);
    if (!item) return;
    props.showDisplayDataWindow({ data: filterData(item), title: item.AlertType });
  };

  const filterData = (rawData) => {
    const {
      AlertType,
      AgencyID,
      Beginning,
      Ending,
      Person,
      OLN,
      OLNState,
      Plate,
      PlateState,
      Address,
      Ranges,
      Notification,
      Priority,
    } = rawData;
    return {
      AlertType,
      AgencyID,
      Beginning,
      Ending,
      Person,
      OLN,
      OLNState,
      Plate,
      PlateState,
      Address,
      Ranges,
      Notification,
      Priority,
    };
  };

  const add = () => props.addBulletin({ type: 'person', object: person });

  const edit = () => props.editBulletin(selection);

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete selected bulletin?')) return;
    try {
      await removeAlert(selection);
      //search();
      await getBulletins();
      //props.notify('Bulletin removed', 'success');
    } catch (err) {
      props.handleError(err, 'Error, Unable to remove bulletin');
    }
  };

  return (
    <div className={classes.wrap}>
      <div className={classes.actions}>
        <PrintsSearch2
          title="Bulletins Records"
          data={data}
          cols={columns}
          hiddenCols={hiddenColumns}
        />
        <Tooltip title="Add Bulletin">
          <Fab size="small" color="secondary" disabled={!bulletinePermission.Create} onClick={add}>
            <AddIcon fontSize="small" />
          </Fab>
        </Tooltip>
        {selection !== null && (
          <>
            <Tooltip title="Edit Bulletin">
              <Fab
                disabled={!bulletinePermission.Edit}
                onClick={edit}
                size="small"
                color="secondary">
                <EditIcon />
              </Fab>
            </Tooltip>
            <Tooltip title="Delete Bulletin">
              <Fab
                size="small"
                color="secondary"
                disabled={!bulletinePermission.Delete}
                onClick={handleDelete}>
                <DeleteIcon />
              </Fab>
            </Tooltip>

            <Tooltip title="View details">
              <Fab size="small" color="secondary" onClick={viewDetails}>
                <InfoIcon />
              </Fab>
            </Tooltip>
          </>
        )}
      </div>

      <div className={classes.gridWrap}>
        {bulletinePermission.Read ? (
          <XGrid
            columns={columns}
            rows={data}
            loading={!loaded}
            rowHeight={38}
            disableMultipleSelection={true}
            showToolbar
            disableColumnFilter
            onSelectionModelChange={(newSelection) => {
              setSelection(newSelection.selectionModel[0]);
            }}
            onColumnVisibilityChange={(col) => setHiddenColumns([...hiddenColumns, col.field])}
          />
        ) : (
          <div style={{ width: '100%', textAlign: 'center' }}>
            <h6 className="pt-2">You don't have permission to see the List</h6>
          </div>
        )}
      </div>
    </div>
  );
}

const mapStateToProps = (state) => {
  return {
    dataUpdate: state.dataUpdate,
  };
};

export default connect(mapStateToProps, {
  handleError,
  showDisplayDataWindow,
  addBulletin,
  notify,
  editBulletin,
})(Bulletins);
